/**
 * apiFetch — Iter-102 · Item 5.
 *
 * Single source of truth for authenticated backend calls from the
 * frontend. Standardizes on cookie-based auth (`credentials:'include'`),
 * which is what /api/auth/me + every other authenticated route on the
 * platform already uses. Adds a defensive bearer-token fallback for
 * anywhere localStorage still carries a legacy `token` value so we
 * never regress a previously-working component during migration.
 *
 * Usage:
 *   const data = await apiFetch('/api/geo/catalog');
 *   const data = await apiFetch('/api/staff/employees', { method: 'POST', body: {...} });
 *
 * If the response is non-2xx, apiFetch throws with { status, detail }.
 */

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export async function apiFetch(path, opts = {}) {
  const {
    method = 'GET',
    body,
    headers = {},
    signal,
    // If the caller opts into a bearer token (e.g. transient impersonation),
    // they can pass token explicitly; otherwise we look at localStorage.
    token = typeof window !== 'undefined' ? localStorage.getItem('token') : null,
    parseJson = true,
    ...rest
  } = opts;

  const finalHeaders = { ...headers };
  const isJsonBody = body && typeof body === 'object' && !(body instanceof FormData);
  if (isJsonBody && !finalHeaders['Content-Type']) {
    finalHeaders['Content-Type'] = 'application/json';
  }
  if (token && !finalHeaders.Authorization) {
    finalHeaders.Authorization = `Bearer ${token}`;
  }

  const res = await fetch(`${BACKEND_URL}${path}`, {
    method,
    credentials: 'include',
    headers: finalHeaders,
    body: isJsonBody ? JSON.stringify(body) : body,
    signal,
    ...rest,
  });

  if (!res.ok) {
    let detail = res.statusText;
    try {
      const j = await res.json();
      detail = j.detail || j.message || detail;
    } catch (_) { /* not JSON */ }
    const err = new Error(detail);
    err.status = res.status;
    err.detail = detail;
    throw err;
  }
  if (!parseJson) return res;
  const contentType = res.headers.get('content-type') || '';
  if (!contentType.includes('application/json')) return null;
  return res.json();
}

/**
 * apiSafeFetch — same as apiFetch but never throws; returns
 * { ok, data, error } instead. Useful in `useEffect` where the caller
 * wants to render an empty state on failure without try/catch noise.
 */
export async function apiSafeFetch(path, opts = {}) {
  try {
    const data = await apiFetch(path, opts);
    return { ok: true, data, error: null };
  } catch (e) {
    return { ok: false, data: null, error: e };
  }
}

export default apiFetch;
